import type {
  ComponentProps,
  ReactNode,
} from 'react'

import {
  Label,
  ProgressBar as AriaProgressBar,
} from 'react-aria-components'

import {
  cva,
  type VariantProps,
} from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

type AriaProgressBarProps =
  ComponentProps<
    typeof AriaProgressBar
  >

const progressIndicatorVariants =
  cva(
    [
      'h-full rounded-full',
      'transition-[width]',
      'duration-300 ease-out',
    ],
    {
      variants: {
        variant: {
          primary:
            'bg-(--color-primary)',

          success:
            'bg-(--color-success)',

          warning:
            'bg-(--color-warning)',

          danger:
            'bg-(--color-danger)',
        },
      },

      defaultVariants: {
        variant: 'primary',
      },
    },
  )

const progressTrackVariants =
  cva(
    [
      'relative w-full',
      'overflow-hidden rounded-full',
      'bg-(--color-surface-muted)',
    ],
    {
      variants: {
        size: {
          small: 'h-1.5',
          medium: 'h-2.5',
          large: 'h-4',
        },
      },

      defaultVariants: {
        size: 'medium',
      },
    },
  )

export type ProgressBarVariant =
  NonNullable<
    VariantProps<
      typeof progressIndicatorVariants
    >['variant']
  >

export type ProgressBarSize =
  NonNullable<
    VariantProps<
      typeof progressTrackVariants
    >['size']
  >

export interface ProgressBarProps
  extends Omit<
    AriaProgressBarProps,
    'children' | 'className'
  > {
  label: ReactNode
  description?: ReactNode

  variant?: ProgressBarVariant
  size?: ProgressBarSize

  showLabel?: boolean
  showValue?: boolean

  className?: string
  trackClassName?: string
  indicatorClassName?: string
}

export function ProgressBar({
  label,
  description,
  variant = 'primary',
  size = 'medium',
  showLabel = true,
  showValue = true,
  className,
  trackClassName,
  indicatorClassName,
  ...props
}: ProgressBarProps) {
  return (
    <AriaProgressBar
      {...props}
      className={cn(
        'w-full',
        className,
      )}
    >
      {({
        percentage,
        valueText,
        isIndeterminate,
      }) => (
        <>
          <div
            className={cn(
              [
                'mb-2 flex',
                'items-center justify-between',
                'gap-4 text-sm',
              ],
              !showLabel &&
                'sr-only',
            )}
          >
            <Label className="min-w-0 truncate font-medium text-(--color-foreground)">
              {label}
            </Label>

            {showValue &&
            !isIndeterminate ? (
              <span className="shrink-0 text-(--color-muted-foreground)">
                {valueText}
              </span>
            ) : null}
          </div>

          <div
            aria-hidden="true"
            className={cn(
              progressTrackVariants({
                size,
              }),
              trackClassName,
            )}
          >
            <div
              className={cn(
                progressIndicatorVariants({
                  variant,
                }),
                isIndeterminate &&
                  'progress-bar-indeterminate',
                indicatorClassName,
              )}
              style={
                isIndeterminate
                  ? undefined
                  : {
                      width: `${percentage}%`,
                    }
              }
            />
          </div>

          {description ? (
            <p className="mt-2 text-xs leading-5 text-(--color-muted-foreground)">
              {description}
            </p>
          ) : null}
        </>
      )}
    </AriaProgressBar>
  )
}